import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Button,
  Form,
  Alert,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';
import {
  FaList,
  FaTimes,
  FaCheck,
  FaMoneyBillWave,
  FaSave,
} from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';

const getAuthToken = () => localStorage.getItem('authToken');

const EditPayment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [payment, setPayment] = useState({
    from_account: '',
    to_account: '',
    from_account_id: '',
    to_account_id: '',
    amount: '',
    date: '',
    payment_number: '',
    description: '',
  });
  const [accounts, setAccounts] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const [showFromDropdown, setShowFromDropdown] = useState(false);
  const [showToDropdown, setShowToDropdown] = useState(false);
  const [filteredFromAccounts, setFilteredFromAccounts] = useState([]);
  const [filteredToAccounts, setFilteredToAccounts] = useState([]);
  const [fromDropdownIndex, setFromDropdownIndex] = useState(-1);
  const [toDropdownIndex, setToDropdownIndex] = useState(-1);
  const fromDropdownRef = useRef(null);
  const toDropdownRef = useRef(null);
  const dateInputRef = useRef(null);

  useEffect(() => {
    fetchPayment();
    fetchAccounts();
    fetchCashAccounts();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        fromDropdownRef.current &&
        !fromDropdownRef.current.contains(event.target)
      ) {
        setShowFromDropdown(false);
      }
      if (
        toDropdownRef.current &&
        !toDropdownRef.current.contains(event.target)
      ) {
        setShowToDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!showFromDropdown) setFromDropdownIndex(-1);
  }, [showFromDropdown]);
  useEffect(() => {
    if (!showToDropdown) setToDropdownIndex(-1);
  }, [showToDropdown]);

  // Keyboard shortcuts: Ctrl+S to save, F2 to focus date
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSubmit();
      }
      if (e.key === 'F2') {
        e.preventDefault();
        if (dateInputRef.current) dateInputRef.current.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [payment]);

  const fetchPayment = async () => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      return;
    }
    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-one-payment?id=${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setPayment(response.data.payment);
    } catch (error) {
      setAlert({
        show: true,
        message: 'Error loading payment details.',
        variant: 'danger',
      });
    }
  };

  const fetchAccounts = async () => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      return;
    }
    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-accounts?all=1`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setAccounts(response.data.data);
      setFilteredToAccounts(response.data.data);
    } catch (error) {
      setAlert({
        show: true,
        message: 'Error loading accounts.',
        variant: 'danger',
      });
    }
  };

  const fetchCashAccounts = async () => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      return;
    }
    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get-accounts`,
        { account_type_ids: [9, 10] },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setCashAccounts(response.data.data);
      setFilteredFromAccounts(response.data.data);
    } catch (error) {
      setAlert({
        show: true,
        message: 'Error loading cash accounts.',
        variant: 'danger',
      });
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!payment.from_account_id || !payment.to_account_id) {
      setAlert({
        show: true,
        message: 'Please select both accounts',
        variant: 'warning',
      });
      return;
    }
    if (!payment.amount || parseFloat(payment.amount) <= 0) {
      setAlert({
        show: true,
        message: 'Please enter a valid amount',
        variant: 'warning',
      });
      return;
    }
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      return;
    }
    const payload = {
      payment_id: id,
      from_account_id: payment.from_account_id,
      to_account_id: payment.to_account_id,
      amount: parseFloat(payment.amount),
      date: payment.date,
      description: payment.description || '',
    };
    setLoading(true);
    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}update-payment`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setAlert({
        show: true,
        message: response.data.message || 'Payment updated successfully!',
        variant: 'success',
      });
      setSaved(true);
      setTimeout(() => navigate('/payment-list'), 600);
    } catch (error) {
      setAlert({
        show: true,
        message:
          error.response?.data?.message ||
          'Error updating payment. Please try again.',
        variant: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFromAccountChange = (e) => {
    const value = e.target.value;
    setPayment({ ...payment, from_account: value, from_account_id: '' });
    if (value.trim()) {
      const filtered = cashAccounts.filter((acc) =>
        acc.name.toLowerCase().includes(value.toLowerCase()),
      );
      setFilteredFromAccounts(filtered);
      setShowFromDropdown(true);
    } else {
      setFilteredFromAccounts(cashAccounts);
      setShowFromDropdown(false);
    }
  };

  const handleToAccountChange = (e) => {
    const value = e.target.value;
    setPayment({ ...payment, to_account: value, to_account_id: '' });
    if (value.trim()) {
      const filtered = accounts.filter((acc) =>
        acc.name.toLowerCase().includes(value.toLowerCase()),
      );
      setFilteredToAccounts(filtered);
      setShowToDropdown(true);
    } else {
      setFilteredToAccounts(accounts);
      setShowToDropdown(false);
    }
  };

  const selectFromAccount = (account) => {
    setPayment({
      ...payment,
      from_account: account.name,
      from_account_id: account.id,
    });
    setShowFromDropdown(false);
  };

  const selectToAccount = (account) => {
    setPayment({
      ...payment,
      to_account: account.name,
      to_account_id: account.id,
    });
    setShowToDropdown(false);
  };

  return (
    <Layout>
      <div
        style={{
          background: '#fff',
          paddingTop: '10px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ maxWidth: '800px', width: '100%' }}>
          {/* Floating Alert (bottom left) */}
          {alert.show && (
            <div
              style={{
                position: 'fixed',
                left: '18px',
                bottom: '18px',
                zIndex: 9999,
                minWidth: '180px',
                maxWidth: '320px',
                pointerEvents: 'auto',
                boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
              }}
            >
              <div
                style={{
                  position: 'relative',
                  borderRadius: '6px',
                  background:
                    alert.variant === 'success'
                      ? '#e6f7e6'
                      : alert.variant === 'danger'
                        ? '#ffeaea'
                        : '#fff8e1',
                  color: '#5c4200',
                  marginBottom: 0,
                  fontWeight: 500,
                  fontSize: '0.92rem',
                  padding: '6px 32px 6px 12px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                  minHeight: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  maxWidth: '320px',
                }}
              >
                <span style={{ flex: 1 }}>{alert.message}</span>
                <button
                  onClick={() =>
                    setAlert({ show: false, message: '', variant: '' })
                  }
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: '8px',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#5c4200',
                    fontSize: '1.1rem',
                    lineHeight: 1,
                    padding: '0 2px',
                    cursor: 'pointer',
                    borderRadius: '2px',
                    height: '22px',
                    width: '22px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>
            </div>
          )}
          {/* Compact Top Bar inside card */}
          <div>
            <div
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '12px 12px 0 0',
                padding: '16px 24px',
                marginBottom: 0,
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                justifyContent: 'space-between',
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
              >
                <FaMoneyBillWave
                  style={{ color: 'white', fontSize: '1.5rem' }}
                />
                <h3
                  style={{
                    color: 'white',
                    margin: 0,
                    fontSize: '1.2rem',
                    fontWeight: '700',
                  }}
                >
                  Edit Payment
                </h3>
              </div>
              <Button
                variant="link"
                onClick={() => navigate('/payment-list')}
                style={{
                  color: 'white',
                  background: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  padding: 0,
                  marginLeft: 'auto',
                  minWidth: 0,
                  minHeight: 0,
                  boxShadow: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
                title="Close"
                aria-label="Close"
              >
                <FaTimes />
              </Button>
            </div>
            <div>
              <Form onSubmit={handleSubmit}>
                {/* Payment Number and Date Row */}
                <div
                  style={{
                    display: 'flex',
                    gap: '20px',
                    marginBottom: '24px',
                    marginTop: '24px',
                  }}
                >
                  <Form.Group style={{ flex: '1' }}>
                    <Form.Label
                      style={{
                        fontWeight: '600',
                        color: '#2c3e50',
                        fontSize: '0.9rem',
                      }}
                    >
                      Payment No.
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={payment.payment_number || '—'}
                      disabled
                      style={{
                        borderRadius: '6px',
                        padding: '10px 12px',
                        border: '1px solid #dee2e6',
                        fontSize: '0.9rem',
                        backgroundColor: '#f8f9fa',
                        color: '#6c757d',
                        fontStyle: 'italic',
                      }}
                    />
                  </Form.Group>
                  <Form.Group style={{ flex: '1' }}>
                    <Form.Label
                      style={{
                        fontWeight: '600',
                        color: '#2c3e50',
                        fontSize: '0.9rem',
                      }}
                    >
                      Date <span style={{ color: '#dc3545' }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={payment.date}
                      onChange={(e) =>
                        setPayment({ ...payment, date: e.target.value })
                      }
                      style={{
                        borderRadius: '6px',
                        padding: '10px 12px',
                        border: '1px solid #dee2e6',
                        fontSize: '0.9rem',
                      }}
                      required
                      ref={dateInputRef}
                    />
                  </Form.Group>
                </div>
                {/* From and To Accounts Row */}
                <div
                  style={{ display: 'flex', gap: '20px', marginBottom: '24px' }}
                >
                  <Form.Group style={{ flex: '1' }} ref={fromDropdownRef}>
                    <Form.Label
                      style={{
                        fontWeight: '600',
                        color: '#2c3e50',
                        fontSize: '0.9rem',
                      }}
                    >
                      From Account (Cash/Bank){' '}
                      <span style={{ color: '#dc3545' }}>*</span>
                    </Form.Label>
                    <div style={{ position: 'relative' }}>
                      <Form.Control
                        type="text"
                        value={payment.from_account}
                        onChange={handleFromAccountChange}
                        placeholder="Search account..."
                        style={{
                          borderRadius: '6px',
                          padding: '10px 12px',
                          border: payment.from_account_id
                            ? '1px solid #28a745'
                            : '1px solid #dee2e6',
                          fontSize: '0.9rem',
                          backgroundColor: payment.from_account_id
                            ? '#f0fff4'
                            : 'white',
                        }}
                        required
                        onKeyDown={(e) => {
                          if (
                            !showFromDropdown ||
                            filteredFromAccounts.length === 0
                          )
                            return;
                          if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            setShowFromDropdown(true);
                            setFromDropdownIndex((prev) =>
                              prev === -1
                                ? 0
                                : (prev + 1) % filteredFromAccounts.length,
                            );
                          } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            setFromDropdownIndex((prev) =>
                              prev === -1
                                ? filteredFromAccounts.length - 1
                                : (prev - 1 + filteredFromAccounts.length) %
                                  filteredFromAccounts.length,
                            );
                          } else if (e.key === 'Enter') {
                            e.preventDefault();
                            if (
                              fromDropdownIndex >= 0 &&
                              fromDropdownIndex < filteredFromAccounts.length
                            ) {
                              selectFromAccount(
                                filteredFromAccounts[fromDropdownIndex],
                              );
                            }
                          } else if (e.key === 'Escape') {
                            setShowFromDropdown(false);
                          }
                        }}
                      />
                      {payment.from_account_id && (
                        <FaCheck
                          style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#28a745',
                          }}
                        />
                      )}
                      {showFromDropdown && filteredFromAccounts.length > 0 && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #dee2e6',
                            borderRadius: '6px',
                            marginTop: '4px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            zIndex: 1000,
                          }}
                        >
                          {filteredFromAccounts.map((acc, idx) => (
                            <div
                              key={acc.id}
                              onClick={() => selectFromAccount(acc)}
                              style={{
                                padding: '10px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f0f0f0',
                                fontSize: '0.9rem',
                                transition: 'background 0.2s',
                                background:
                                  idx === fromDropdownIndex
                                    ? '#e9ecef'
                                    : 'white',
                              }}
                            >
                              {acc.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Form.Group>
                  <Form.Group style={{ flex: '1' }} ref={toDropdownRef}>
                    <Form.Label
                      style={{
                        fontWeight: '600',
                        color: '#2c3e50',
                        fontSize: '0.9rem',
                      }}
                    >
                      To Account <span style={{ color: '#dc3545' }}>*</span>
                    </Form.Label>
                    <div style={{ position: 'relative' }}>
                      <Form.Control
                        type="text"
                        value={payment.to_account}
                        onChange={handleToAccountChange}
                        placeholder="Search account..."
                        style={{
                          borderRadius: '6px',
                          padding: '10px 12px',
                          border: payment.to_account_id
                            ? '1px solid #28a745'
                            : '1px solid #dee2e6',
                          fontSize: '0.9rem',
                          backgroundColor: payment.to_account_id
                            ? '#f0fff4'
                            : 'white',
                        }}
                        required
                        onKeyDown={(e) => {
                          if (
                            !showToDropdown ||
                            filteredToAccounts.length === 0
                          )
                            return;
                          if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            setShowToDropdown(true);
                            setToDropdownIndex((prev) =>
                              prev === -1
                                ? 0
                                : (prev + 1) % filteredToAccounts.length,
                            );
                          } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            setToDropdownIndex((prev) =>
                              prev === -1
                                ? filteredToAccounts.length - 1
                                : (prev - 1 + filteredToAccounts.length) %
                                  filteredToAccounts.length,
                            );
                          } else if (e.key === 'Enter') {
                            e.preventDefault();
                            if (
                              toDropdownIndex >= 0 &&
                              toDropdownIndex < filteredToAccounts.length
                            ) {
                              selectToAccount(
                                filteredToAccounts[toDropdownIndex],
                              );
                            }
                          } else if (e.key === 'Escape') {
                            setShowToDropdown(false);
                          }
                        }}
                      />
                      {payment.to_account_id && (
                        <FaCheck
                          style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#28a745',
                          }}
                        />
                      )}
                      {showToDropdown && filteredToAccounts.length > 0 && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #dee2e6',
                            borderRadius: '6px',
                            marginTop: '4px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            zIndex: 1000,
                          }}
                        >
                          {filteredToAccounts.map((acc, idx) => (
                            <div
                              key={acc.id}
                              onClick={() => selectToAccount(acc)}
                              style={{
                                padding: '10px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f0f0f0',
                                fontSize: '0.9rem',
                                transition: 'background 0.2s',
                                background:
                                  idx === toDropdownIndex ? '#e9ecef' : 'white',
                              }}
                            >
                              {acc.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Form.Group>
                </div>
                {/* Amount and Description Row */}
                <div
                  style={{ display: 'flex', gap: '20px', marginBottom: '24px' }}
                >
                  <Form.Group style={{ flex: '1' }}>
                    <Form.Label
                      style={{
                        fontWeight: '600',
                        color: '#2c3e50',
                        fontSize: '1rem',
                      }}
                    >
                      Amount <span style={{ color: '#dc3545' }}>*</span>
                    </Form.Label>
                    <div style={{ position: 'relative' }}>
                      <span
                        style={{
                          position: 'absolute',
                          left: '16px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#667eea',
                          fontWeight: '700',
                          fontSize: '1.2rem',
                        }}
                      >
                        ₹
                      </span>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={payment.amount}
                        onChange={(e) =>
                          setPayment({ ...payment, amount: e.target.value })
                        }
                        onWheel={(e) => e.target.blur()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') e.preventDefault();
                        }}
                        placeholder="0.00"
                        style={{
                          borderRadius: '8px',
                          padding: '14px 16px 14px 40px',
                          border: '2px solid #667eea',
                          fontSize: '1.1rem',
                          fontWeight: '600',
                          backgroundColor: '#f8f9ff',
                          color: '#2c3e50',
                        }}
                        required
                      />
                    </div>
                  </Form.Group>
                  <Form.Group style={{ flex: '1' }}>
                    <Form.Label
                      style={{
                        fontWeight: '600',
                        color: '#2c3e50',
                        fontSize: '0.9rem',
                      }}
                    >
                      Description
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={1}
                      value={payment.description}
                      onChange={(e) =>
                        setPayment({ ...payment, description: e.target.value })
                      }
                      onFocus={(e) => (e.target.style.height = '72px')}
                      onBlur={(e) => {
                        if (!e.target.value) e.target.style.height = '55px';
                      }}
                      onInput={(e) => {
                        if (e.target.value) e.target.style.height = '72px';
                        else e.target.style.height = '55px';
                      }}
                      placeholder="Add any notes or description..."
                      style={{
                        borderRadius: '6px',
                        padding: '10px 12px',
                        border: '1px solid #dee2e6',
                        fontSize: '0.9rem',
                        resize: 'none',
                        height: payment.description ? '72px' : '55px',
                        transition: 'height 0.2s',
                      }}
                    />
                  </Form.Group>
                </div>
                {/* Submit Button */}
                <div
                  style={{
                    display: 'flex',
                    gap: '10px',
                    justifyContent: 'flex-end',
                  }}
                >
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="save-tooltip">Save (Ctrl+S)</Tooltip>}
                  >
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={loading || saved}
                      style={{
                        background:
                          'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '10px 24px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        minWidth: '140px',
                        justifyContent: 'center',
                      }}
                    >
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <FaSave /> Save Changes
                        </>
                      )}
                    </Button>
                  </OverlayTrigger>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EditPayment;
